/* eslint-disable no-nested-ternary, react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// @mui
import {
  Container,
  Grid,
  Typography,
  Stack,
  Button,
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Divider,
  useTheme,
  alpha,
  LinearProgress,
  ButtonGroup,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { toast } from 'react-toastify';
import { ArrowLeft, Copy, Smartphone, Globe, Calendar, Check, Download, Trash2, Edit2 } from 'lucide-react';

// components
import Page from '../components/Page';
import { BaseOptionChart } from '../components/chart';
import { fCurrency, fNumber } from '../utils/formatNumber';
import * as campaignsApi from '../api/campaigns.api';

// ----------------------------------------------------------------------

export default function CampaignDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [campaign, setCampaign] = useState(null);
  const [users, setUsers] = useState([]);
  const [charts, setCharts] = useState(null);
  const [chartPeriod, setChartPeriod] = useState('daily'); // 'daily' | 'weekly' | 'monthly'
  const [chartMetric, setChartMetric] = useState('installs'); // 'installs' | 'revenue'
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editBudget, setEditBudget] = useState(0);
  const [editSpent, setEditSpent] = useState(0);
  const [editBaseUrl, setEditBaseUrl] = useState('');
  const [editStatus, setEditStatus] = useState('Active');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editPlatform, setEditPlatform] = useState('Android');
  const [editChannel, setEditChannel] = useState('Google Ads');
  const [editLoading, setEditLoading] = useState(false);

  const fetchCampaignDetails = async () => {
    try {
      setLoading(true);
      const response = await campaignsApi.getCampaign(id);
      if (response.data && response.data.success) {
        const campData = response.data.data;
        setCampaign(campData);
        setUsers(campData.usersList || []);

        const totalClicks = campData.clicks;
        const totalInstalls = campData.installs;
        const totalRevenue = campData.revenue;

        const daysCount = 10;
        const mockDates = [];
        const mockClicks = [];
        const mockInstalls = [];
        const mockFunding = [];

        const baseDate = new Date(campData.createdAt || Date.now());
        for (let i = daysCount - 1; i >= 0; i -= 1) {
          const d = new Date(baseDate);
          d.setDate(baseDate.getDate() - i);
          mockDates.push(d.toISOString().split('T')[0]);

          const dailyClicks = Math.floor((totalClicks / daysCount) * (0.7 + Math.random() * 0.6));
          const dailyInstalls = Math.floor((totalInstalls / daysCount) * (0.7 + Math.random() * 0.6));
          const dailyFunding = Math.floor((totalRevenue / daysCount) * (0.7 + Math.random() * 0.6));

          mockClicks.push(dailyClicks);
          mockInstalls.push(dailyInstalls);
          mockFunding.push(dailyFunding);
        }

        setCharts({
          daily: {
            dates: mockDates,
            clicks: mockClicks,
            installs: mockInstalls,
            funding: mockFunding,
          },
          weekly: {
            dates: ['Week A', 'Week B', 'Week C'],
            clicks: [Math.floor(totalClicks * 0.3), Math.floor(totalClicks * 0.4), Math.floor(totalClicks * 0.3)],
            installs: [
              Math.floor(totalInstalls * 0.3),
              Math.floor(totalInstalls * 0.4),
              Math.floor(totalInstalls * 0.3),
            ],
            funding: [Math.floor(totalRevenue * 0.3), Math.floor(totalRevenue * 0.4), Math.floor(totalRevenue * 0.3)],
          },
          monthly: {
            dates: [new Date(campData.startDate || Date.now()).toLocaleString('default', { month: 'short' })],
            clicks: [totalClicks],
            installs: [totalInstalls],
            funding: [totalRevenue],
          },
        });
      }
    } catch (error) {
      toast.error('Failed to fetch campaign details from server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaignDetails();
  }, [id]);

  // Compute stats dynamically
  const spendPercent = useMemo(() => {
    if (!campaign || campaign.budget <= 0) return 0;
    return (campaign.spent / campaign.budget) * 100;
  }, [campaign]);

  const convRate = useMemo(() => {
    if (!campaign || campaign.clicks <= 0) return 0;
    return (campaign.installs / campaign.clicks) * 100;
  }, [campaign]);

  const roi = useMemo(() => {
    if (!campaign || campaign.spent <= 0) return 0;
    return ((campaign.revenue - campaign.spent) / campaign.spent) * 100;
  }, [campaign]);

  const durationDays = useMemo(() => {
    if (!campaign || !campaign.startDate || !campaign.endDate) return 0;
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    return Math.max(0, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
  }, [campaign]);

  // Construct sharing link
  const attributionUrl = useMemo(() => {
    if (!campaign) return '';
    const params = new URLSearchParams();
    if (campaign.utmSource) params.append('utm_source', campaign.utmSource);
    if (campaign.utmMedium) params.append('utm_medium', campaign.utmMedium);
    if (campaign.utmCampaign) params.append('utm_campaign', campaign.utmCampaign);
    const queryStr = params.toString();
    return queryStr ? `${campaign.baseUrl}?${queryStr}` : campaign.baseUrl;
  }, [campaign]);

  const handleCopyLink = () => {
    if (!attributionUrl) return;
    navigator.clipboard.writeText(attributionUrl);
    setCopied(true);
    toast.success('Attribution link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportUsersCSV = () => {
    if (users.length === 0) {
      toast.warning('No registered users available to export');
      return;
    }
    const headers = ['name', 'email', 'registeredAt', 'platform', 'funded', 'spent', 'balance', 'status'];
    const csvRows = [headers.join(',')];

    users.forEach((row) => {
      const values = headers.map((header) => {
        const val = row[header] !== undefined ? row[header] : '';
        const escaped = String(val).replace(/"/g, '\\"');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${campaign.name.toLowerCase().replace(/\s+/g, '_')}_users_ledger.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Acquisition user ledger exported as CSV');
  };

  const handleDeleteCampaign = async () => {
    // eslint-disable-next-line no-alert
    if (
      window.confirm(
        'Are you sure you want to delete this campaign? This will remove all associated user registrations.'
      )
    ) {
      try {
        const response = await campaignsApi.deleteCampaign(id);
        if (response.data && response.data.success) {
          toast.success('Campaign deleted successfully');
          navigate('/dashboard/campaigns');
        }
      } catch (error) {
        toast.error('Failed to delete campaign from server');
      }
    }
  };

  const handleOpenEditDialog = () => {
    setEditName(campaign.name || '');
    setEditDescription(campaign.description || '');
    setEditBudget(campaign.budget || 0);
    setEditSpent(campaign.spent || 0);
    setEditBaseUrl(campaign.baseUrl || '');
    setEditStatus(campaign.status || 'Active');
    setEditStartDate(campaign.startDate || '');
    setEditEndDate(campaign.endDate || '');
    setEditPlatform(campaign.platform || 'Android');
    setEditChannel(campaign.channel || 'Google Ads');
    setOpenEditDialog(true);
  };

  const handleSaveCampaign = async (e) => {
    if (e) e.preventDefault();
    if (!editName.trim()) {
      toast.warning('Campaign name is required');
      return;
    }
    setEditLoading(true);
    try {
      const response = await campaignsApi.updateCampaign(id, {
        name: editName.trim(),
        description: editDescription.trim(),
        budget: Number(editBudget),
        spent: Number(editSpent),
        baseUrl: editBaseUrl.trim(),
        status: editStatus,
        startDate: editStartDate || null,
        endDate: editEndDate || null,
        platform: editPlatform,
        channel: editChannel,
      });
      if (response.data && response.data.success) {
        toast.success('Campaign updated successfully');
        setOpenEditDialog(false);
        await fetchCampaignDetails();
      } else {
        toast.error('Failed to update campaign');
      }
    } catch (error) {
      toast.error('Failed to update campaign');
    }
    setEditLoading(false);
  };

  // Determine current active timeline data based on selected interval
  const activeTimelineData = useMemo(() => {
    if (!charts) return { dates: [], clicks: [], installs: [], funding: [] };
    return charts[chartPeriod] || charts.daily || { dates: [], clicks: [], installs: [], funding: [] };
  }, [charts, chartPeriod]);

  // ApexCharts config
  const chartOptionsConsolidated = useMemo(() => {
    const baseOptions = BaseOptionChart();
    const isInstalls = chartMetric === 'installs';
    return {
      ...baseOptions,
      stroke: { width: 3, curve: 'smooth' },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0.05,
          stops: [20, 100],
        },
      },
      labels: activeTimelineData.dates,
      xaxis: {
        type: chartPeriod === 'daily' ? 'datetime' : 'category',
        labels: {
          style: { colors: theme.palette.text.secondary },
        },
      },
      yaxis: {
        labels: {
          style: { colors: theme.palette.text.secondary },
          formatter: (val) => (isInstalls ? fNumber(val) : `₦${fCurrency(val)}`),
        },
      },
      colors: [isInstalls ? theme.palette.info.main : theme.palette.success.main],
      grid: { strokeDashArray: 3 },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (y) =>
            typeof y !== 'undefined' ? (isInstalls ? `${y.toLocaleString()} installs` : `₦ ${fCurrency(y)} funded`) : y,
        },
      },
    };
  }, [activeTimelineData.dates, chartPeriod, chartMetric, theme]);

  if (!campaign) {
    return (
      <Page title="Loading Campaign...">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Typography variant="body1">Loading campaign analytics...</Typography>
        </Box>
      </Page>
    );
  }

  // Simple aggregation for UI
  const totalBalance = users.reduce((acc, u) => acc + (u.balance || 0), 0);

  return (
    <Page title={`${campaign.name} - Campaign Details`}>
      <Container maxWidth="xl">
        {/* Navigation Back Panel */}
        <Box mb={2}>
          <Button
            startIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/dashboard/campaigns')}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: 'transparent',
              },
            }}
          >
            Back to Campaigns
          </Button>
        </Box>

        {/* Simplified Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          mb={4}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
                {campaign.name}
              </Typography>
              <Chip
                label={campaign.status}
                size="small"
                color={campaign.status === 'Active' ? 'success' : campaign.status === 'Paused' ? 'warning' : 'default'}
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1.5} color="text.secondary">
              <Box display="flex" alignItems="center" gap={0.5}>
                {campaign.platform === 'Android' && <Smartphone size={15} color="#A4C639" />}
                {campaign.platform === 'iOS' && <Smartphone size={15} color="#555555" />}
                {campaign.platform === 'Web' && <Globe size={15} color="#1890FF" />}
                <Typography variant="body2">{campaign.platform} Target</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ height: 12, my: 'auto' }} />
              <Typography variant="body2">{campaign.channel}</Typography>
              {campaign.startDate && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ height: 12, my: 'auto' }} />
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Calendar size={13} />
                    <Typography variant="body2">
                      {campaign.startDate} to {campaign.endDate} ({durationDays} days)
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Edit2 size={16} />}
              onClick={handleOpenEditDialog}
              sx={{
                borderColor: '#DFE3E8',
                py: 1,
                px: 2.5,
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              Edit Campaign
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Trash2 size={16} />}
              onClick={handleDeleteCampaign}
              sx={{
                borderColor: '#DFE3E8',
                color: theme.palette.error.main,
                py: 1,
                px: 2.5,
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.04),
                  borderColor: theme.palette.error.main,
                },
              }}
            >
              Delete Campaign
            </Button>
          </Stack>
        </Stack>

        {/* Simplified Performance metrics */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <AppleMetricCard
              label="Ad Spend Tracker"
              value={`₦${fCurrency(campaign.spent)}`}
              description={`₦${fCurrency(campaign.budget)} budget (${spendPercent.toFixed(0)}% used)`}
              progress={spendPercent}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AppleMetricCard
              label="App Installs"
              value={fNumber(campaign.installs)}
              description={`${fNumber(campaign.clicks)} clicks (${convRate.toFixed(1)}% Conversion)`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AppleMetricCard
              label="Revenue Generated"
              value={`₦${fCurrency(campaign.revenue)}`}
              description={`Estimated ROI: ${roi >= 0 ? '+' : ''}${roi.toFixed(0)}%`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AppleMetricCard
              label="Active Wallet Balances"
              value={`₦${fCurrency(totalBalance)}`}
              description={`Attributed from ${users.length} active customer wallets`}
            />
          </Grid>
        </Grid>

        {/* Simplified Single Chart Panel */}
        <Grid container spacing={4} mb={4}>
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #E5E5E7', boxShadow: 'none', borderRadius: '12px' }}>
              <CardHeader
                title="Performance Timeline"
                subheader="Select a metric and a time interval below to inspect campaign metrics over time."
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                action={
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    {/* Metric Select */}
                    <ToggleButtonGroup
                      value={chartMetric}
                      exclusive
                      onChange={(e, val) => val && setChartMetric(val)}
                      size="small"
                      sx={{ '& .MuiToggleButton-root': { py: 0.5, px: 2, textTransform: 'none', fontWeight: 600 } }}
                    >
                      <ToggleButton value="installs">Installs</ToggleButton>
                      <ToggleButton value="revenue">Revenue</ToggleButton>
                    </ToggleButtonGroup>

                    {/* Time Frame Select */}
                    <ButtonGroup size="small" aria-label="outlined primary button group">
                      <Button
                        onClick={() => setChartPeriod('daily')}
                        variant={chartPeriod === 'daily' ? 'contained' : 'outlined'}
                        sx={{ textTransform: 'none' }}
                      >
                        Daily
                      </Button>
                      <Button
                        onClick={() => setChartPeriod('weekly')}
                        variant={chartPeriod === 'weekly' ? 'contained' : 'outlined'}
                        sx={{ textTransform: 'none' }}
                      >
                        Weekly
                      </Button>
                      <Button
                        onClick={() => setChartPeriod('monthly')}
                        variant={chartPeriod === 'monthly' ? 'contained' : 'outlined'}
                        sx={{ textTransform: 'none' }}
                      >
                        Monthly
                      </Button>
                    </ButtonGroup>
                  </Stack>
                }
              />
              <Divider sx={{ mt: 1.5 }} />
              <CardContent sx={{ py: 3 }}>
                <ReactApexChart
                  type="area"
                  series={[
                    {
                      name: chartMetric === 'installs' ? 'App Installs' : 'Wallet Funded (₦)',
                      data: chartMetric === 'installs' ? activeTimelineData.installs : activeTimelineData.funding,
                    },
                  ]}
                  options={chartOptionsConsolidated}
                  height={320}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Customer Ledger Table & Clean Link Box */}
        <Grid container spacing={4}>
          {/* Simple Link Copy Box */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                border: '1px solid #E5E5E7',
                boxShadow: 'none',
                borderRadius: '12px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <CardHeader
                  title="Attribution Link"
                  subheader="Distribute this unique URL to track clicks, installs, and registration wallets."
                  titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                />
                <Divider />
                <CardContent sx={{ pt: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                    UTM parameters: ?utm_source={campaign.utmSource}&utm_campaign={campaign.utmCampaign}
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: '#F9FAFB',
                      borderColor: '#E5E5E7',
                      wordBreak: 'break-all',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      mb: 2.5,
                    }}
                  >
                    {attributionUrl}
                  </Paper>
                </CardContent>
              </Box>

              <Box p={3} borderTop="1px solid #E5E5E7">
                <Stack direction="row" spacing={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleCopyLink}
                    startIcon={copied ? <Check size={16} /> : <Copy size={16} />}
                    sx={{ borderColor: '#DFE3E8', color: 'text.primary', textTransform: 'none', fontWeight: 600 }}
                  >
                    Copy Link
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    component="a"
                    href={attributionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ backgroundColor: theme.palette.primary.main, textTransform: 'none', fontWeight: 600 }}
                  >
                    Test Link
                  </Button>
                </Stack>
              </Box>
            </Card>
          </Grid>

          {/* Acquired Users table */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ border: '1px solid #E5E5E7', boxShadow: 'none', borderRadius: '12px' }}>
              <CardHeader
                title={`Acquired Customer Wallets (${users.length})`}
                subheader="Individual customer wallets generated and funded through this ad click funnel."
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download size={15} />}
                    onClick={handleExportUsersCSV}
                    sx={{
                      borderColor: '#DFE3E8',
                      color: 'text.secondary',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    Export Ledger
                  </Button>
                }
              />
              <Divider />
              <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 600 }}>
                  <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableRow>
                      <TableCell sx={{ pl: 3 }}>User</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell align="right">Total Funded</TableCell>
                      <TableCell align="right">Total Spent</TableCell>
                      <TableCell align="right">Wallet Balance</TableCell>
                      <TableCell sx={{ pr: 3 }}>Activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ pl: 3 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{user.registeredAt}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.platform}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.success.dark }}>
                          ₦{fCurrency(user.funded)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.error.dark }}>
                          ₦{fCurrency(user.spent)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                          ₦{fCurrency(user.balance)}
                        </TableCell>
                        <TableCell sx={{ pr: 3 }}>
                          <Chip
                            label={user.status}
                            size="small"
                            color={user.status === 'Active' ? 'success' : 'default'}
                            sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            No customer wallets attributed to this campaign.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          </Grid>
        </Grid>
        {/* Modal: Edit Campaign */}
        <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
          <form onSubmit={handleSaveCampaign}>
            <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>Edit Ad Campaign</DialogTitle>
            <DialogContent dividers sx={{ py: 2 }}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label="Campaign Name"
                  placeholder="e.g. Summer Promo 2026"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  label="Description"
                  placeholder="e.g. Ad Campaign description..."
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  variant="outlined"
                  multiline
                  rows={2}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="OS Platform Target"
                      value={editPlatform}
                      onChange={(e) => setEditPlatform(e.target.value)}
                      variant="outlined"
                    >
                      <MenuItem value="Android">Android</MenuItem>
                      <MenuItem value="iOS">iOS</MenuItem>
                      <MenuItem value="Web">Web</MenuItem>
                      <MenuItem value="All">All (Multi-platform)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Acquisition Channel"
                      value={editChannel}
                      onChange={(e) => setEditChannel(e.target.value)}
                      variant="outlined"
                    >
                      <MenuItem value="Google Ads">Google Ads</MenuItem>
                      <MenuItem value="Facebook Ads">Facebook Ads</MenuItem>
                      <MenuItem value="TikTok">TikTok</MenuItem>
                      <MenuItem value="Instagram">Instagram</MenuItem>
                      <MenuItem value="Twitter">Twitter / X</MenuItem>
                      <MenuItem value="Influencer">Influencer</MenuItem>
                      <MenuItem value="Email Newsletter">Email Newsletter</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Budget Allocation"
                      placeholder="Budget in Naira"
                      value={editBudget}
                      onChange={(e) => setEditBudget(e.target.value)}
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      }}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Ad Spent Tracker"
                      placeholder="Spent in Naira"
                      value={editSpent}
                      onChange={(e) => setEditSpent(e.target.value)}
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      value={editStartDate}
                      onChange={(e) => setEditStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      value={editEndDate}
                      onChange={(e) => setEditEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  select
                  label="Campaign Status"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  variant="outlined"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Paused">Paused</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </TextField>

                {/* <TextField
                  fullWidth
                  label="App Landing URL / Base URL"
                  value={editBaseUrl}
                  onChange={(e) => setEditBaseUrl(e.target.value)}
                  variant="outlined"
                /> */}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setOpenEditDialog(false)} color="inherit">
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Page>
  );
}

// Minimalistic Apple style metric display widget
function AppleMetricCard({ label, value, description, progress }) {
  const theme = useTheme();

  return (
    <Card
      sx={{
        p: 3,
        border: '1px solid #E5E5E7',
        boxShadow: 'none',
        borderRadius: '12px',
        backgroundColor: '#FFFFFF',
        position: 'relative',
      }}
    >
      <Stack spacing={1.5}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
        >
          {label}
        </Typography>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-1px' }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontWeight: 500 }}>
            {description}
          </Typography>
        </Box>
        {progress !== undefined && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, progress)}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: '#F4F6F8',
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.primary.main,
              },
            }}
          />
        )}
      </Stack>
    </Card>
  );
}
